import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { InlineSpinner } from './LoadingState';
import { VisuallyHidden } from './VisuallyHidden';
import { getCampaign } from '../api/api'; // Adjust the import path as needed

const RefreshButton = ({
  onRefresh,
  label = 'Refresh',
  className = '',
  disabled = false,
  setAlert,
  throttle = 1000,
  showTemplateDropdown = false,
  campaign = null,
  currentTemplate = null
}) => {
  const [loading, setLoading] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [isLoadingTemplates, setIsLoadingTemplates] = useState(false);
  // The menu renders in a portal with fixed positioning so ancestor
  // overflow/stacking contexts can never clip or overlap it. Right-aligned
  // to the trigger, clamped to the viewport for triggers near either edge.
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0 });
  const lastRef = useRef(0);
  const statusRef = useRef(null);
  const dropdownRef = useRef(null);
  const menuRef = useRef(null);
  const MENU_WIDTH = 224; // w-56

  const updateMenuPosition = useCallback(() => {
    if (!dropdownRef.current) return;
    const rect = dropdownRef.current.getBoundingClientRect();
    const left = Math.max(
      8,
      Math.min(rect.right - MENU_WIDTH, window.innerWidth - MENU_WIDTH - 8)
    );
    setMenuPos({ top: rect.bottom + 8, left });
  }, []);

  // Close dropdown when clicking outside; keep it anchored on scroll/resize
  useEffect(() => {
    if (!isDropdownOpen) return;

    const handleClickOutside = (event) => {
      if (
        dropdownRef.current && !dropdownRef.current.contains(event.target) &&
        menuRef.current && !menuRef.current.contains(event.target)
      ) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('scroll', updateMenuPosition, true);
    window.addEventListener('resize', updateMenuPosition);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', updateMenuPosition, true);
      window.removeEventListener('resize', updateMenuPosition);
    };
  }, [isDropdownOpen, updateMenuPosition]);

  // Templates are cached per campaign — clear them when the campaign changes
  // so the next dropdown open refetches for the new campaign.
  useEffect(() => {
    setTemplates([]);
  }, [campaign]);

  // Fetch templates when dropdown is opened (templates.length is a dep so the
  // reset above triggers a refetch while the dropdown is open)
  useEffect(() => {
    if (isDropdownOpen && showTemplateDropdown && templates.length === 0) {
      fetchTemplates();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDropdownOpen, showTemplateDropdown, campaign, templates.length]);

  const fetchTemplates = async () => {
    setIsLoadingTemplates(true);
    try {
      if (!campaign) {
        console.warn('No campaign provided to fetch templates');
        setTemplates([]);
        return;
      }

      const response = await getCampaign(campaign);
      setTemplates(response.templates || []);
    } catch (error) {
      console.error('Error fetching templates for campaign:', error);
      if (setAlert) {
        setAlert({
          variant: 'error',
          heading: 'Error',
          text: `Failed to load templates for campaign "${campaign}". Please try again.`
        });
      }
      setTemplates([]);
    } finally {
      setIsLoadingTemplates(false);
    }
  };

  const handleClick = async (template = null) => {
    if (loading || disabled) return;
    const now = Date.now();
    if (now - lastRef.current < throttle) return;
    lastRef.current = now;

    setLoading(true);
    setIsDropdownOpen(false);
    if (statusRef.current) statusRef.current.textContent = 'Refreshing...';

    // Use currentTemplate if no template is provided (main button click)
    const templateToUse = template || currentTemplate || '';

    try {
      await onRefresh(templateToUse);
      if (statusRef.current) statusRef.current.textContent = 'Refresh complete';

      if (setAlert && templateToUse) {
        setAlert({
          variant: 'success',
          heading: 'Success',
          text: `Summary refreshed using template: ${typeof templateToUse === 'object' ? templateToUse.templateName || templateToUse.name || templateToUse.title : templateToUse}`
        });
      }
    } catch (err) {
      console.error('Refresh failed', err);
      if (setAlert) {
        setAlert({
          variant: 'error',
          heading: 'Error',
          text: 'Unable to refresh. Please try again.'
        });
      }
      if (statusRef.current) statusRef.current.textContent = 'Refresh failed';
    } finally {
      setLoading(false);
      setTimeout(() => {
        if (statusRef.current) statusRef.current.textContent = '';
      }, 1000);
    }
  };

  const handleTemplateSelect = (template) => {
    handleClick(template);
  };

  const toggleDropdown = () => {
    if (!isDropdownOpen) {
      updateMenuPosition();
    }
    setIsDropdownOpen((v) => !v);
  };

  if (!showTemplateDropdown) {
    // Original simple refresh button
    return (
      <button
        onClick={() => handleClick()}
        disabled={loading || disabled}
        className={`btn-outline disabled:opacity-50 ${className}`}
        aria-busy={loading}
      >
        {loading ? (
          <InlineSpinner light className="mr-2" />
        ) : (
          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
            />
          </svg>
        )}
        {loading ? 'Refreshing...' : label}
        <VisuallyHidden>
          <span ref={statusRef} aria-live="polite" />
        </VisuallyHidden>
      </button>
    );
  }

  return (
    <div className="relative inline-block text-left" ref={dropdownRef}>
      <div className="inline-flex items-stretch rounded-full border border-gray-200 bg-white overflow-hidden">
        {/* Main refresh button */}
        <button
          onClick={() => handleClick()}
          disabled={loading || disabled}
          className={`inline-flex items-center pl-4 pr-3 py-2 text-sm font-semibold text-gray-800 hover:bg-gray-50 disabled:opacity-50 whitespace-nowrap transition-colors ${className}`}
          aria-busy={loading}
        >
          {loading ? (
            <InlineSpinner light className="mr-2" />
          ) : (
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
              />
            </svg>
          )}
          {loading ? 'Refreshing...' : label}
        </button>

        <div className="w-px bg-gray-200 my-1.5" />

        {/* Dropdown toggle */}
        <button
          onClick={toggleDropdown}
          disabled={loading || disabled}
          className="inline-flex items-center px-2.5 text-gray-500 hover:bg-gray-50 disabled:opacity-50 transition-colors"
          aria-haspopup="true"
          aria-expanded={isDropdownOpen}
        >
          <svg
            className={`h-3 w-3 transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
      </div>

      {/* Dropdown menu — portaled so ancestor overflow/z-index can't clip it */}
      {isDropdownOpen && createPortal(
        <div
          ref={menuRef}
          className="popup-surface fixed z-50 w-56 rounded-lg border shadow-lg bg-white focus:outline-none"
          style={{ top: menuPos.top, left: menuPos.left }}
        >
          <div className="py-1">
            <div className="px-4 py-2 text-sm text-gray-700 border-b border-gray-100">
              <strong>Select Template:</strong>
            </div>
            
            {isLoadingTemplates ? (
              <div className="px-4 py-2 text-sm text-gray-500">
                <div className="flex items-center">
                  <InlineSpinner light className="mr-2" />
                  Loading templates...
                </div>
              </div>
            ) : templates.length > 0 ? (
              templates.map((template, index) => (
                <button
                  key={index}
                  onClick={() => handleTemplateSelect(template)}
                  className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900 focus:outline-none focus:bg-gray-100"
                  disabled={loading}
                >
                  {typeof template === 'object' ? template.templateName || template.name || template.title || template.label : template}
                </button>
              ))
            ) : (
              <div className="px-4 py-2 text-sm text-gray-500">
                No templates available
              </div>
            )}
          </div>
        </div>,
        document.body
      )}

      <VisuallyHidden>
        <span ref={statusRef} aria-live="polite" />
      </VisuallyHidden>
    </div>
  );
};

export default RefreshButton;